import { createFileRoute, redirect } from "@tanstack/react-router";

import { auth } from "@/lib/auth";
import { adminData } from "@/data/navData";
import { visibleNavItems } from "@/lib/nav-visibility";
import { ensureMyPermissions } from "@/hooks/api/authorization/useAuthorization";

const DASHBOARD_URL = "/admin";

export const Route = createFileRoute("/admin/_layout/")({
  beforeLoad: async () => {
    // Unauthenticated: leave it to the layout's own client-side redirect to
    // /login — computing capabilities here would 401 and misfire below.
    if (!auth.getToken()) return;

    const capabilities = await ensureMyPermissions();
    const items = visibleNavItems(adminData.mainMenuItems, {
      capabilities,
      isEnabled: useFeatureStore.getState().isEnabled,
      settings: useOrgSettingsStore.getState().settings,
    });
    const dashboardVisible = items.some((item) => item.url === DASHBOARD_URL);
    if (dashboardVisible) return;

    const firstReachable = items.find((item) => item.url !== DASHBOARD_URL);
    throw redirect({ to: firstReachable?.url ?? "/unauthorized" });
  },
  component: AdminDashboard,
});

import { useMemo } from "react";
import { Link } from "@tanstack/react-router";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { StatCard } from "@/components/reusable-components/stat-card";
import { PendingLeaveRequests } from "@/components/admin/dashboard/PendingLeaveRequests";
import { TasksNeedingAttention } from "@/components/admin/dashboard/TasksNeedingAttention";
import { UpcomingHolidays } from "@/components/admin/dashboard/UpcomingHolidays";
import { TodaysAttendance } from "@/components/admin/dashboard/TodaysAttendance";

import useFetchUser from "@/hooks/api/user/useFetchUser";
import useFetchTimeSheet from "@/hooks/api/timesheet/useFetchTimeSheets";
import useFetchLeaveRequests from "@/hooks/api/leave/leave-request/useFetchLeaveRequests";
import useFetchTasks from "@/hooks/api/tasks/useFetchTask";
import useFetchAdvances from "@/hooks/api/salaries/advances/useFetchAdvances";
import useFetchBranches from "@/hooks/api/organization/organization-branches/useFetchBranches";
import useFetchDepartments from "@/hooks/api/organization/organization-departments/useFetchDepartments";

import { useAuthStore } from "@/stores/useAuthStore";
import { useFeatureStore } from "@/stores/useFeatureStore";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import { nowInOrgTimezone } from "@/lib/date-utils";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can, canAnyIn } from "@/lib/permissions";
import { receivesApprovals } from "@/lib/supervisor";
import { SupervisorAccessBanner } from "@/components/supervisor/dashboard/SupervisorAccessBanner";

// Best-effort headcount for today's check-in snapshot -- a page this size
// covers the overwhelming majority of orgs; beyond it the ratio undercounts
// rather than paginating through every timesheet on every dashboard load.
const ATTENDANCE_SNAPSHOT_SIZE = 200;
const ADVANCES_SNAPSHOT_SIZE = 100;

const cardClassName = "bg-linear-to-t from-primary/5 to-card shadow-xs h-full";

function AdminDashboard() {
  const user = useAuthStore((s) => s.user);
  const { isEnabled } = useFeatureStore();
  const { data: capabilities } = useMyPermissions();

  const attendanceEnabled = isEnabled("attendance");
  const leaveEnabled = isEnabled("leave");
  const tasksEnabled = isEnabled("tasks");
  const salariesEnabled = isEnabled("salaries");

  const canViewLeaveRequests = can(capabilities, "leave.request.view");
  const canManageTasks = can(capabilities, "task.task.manage");
  const canManageAdvances = can(capabilities, "salary.advance.manage");

  const canSeeEmployees = canAnyIn(capabilities, "users");
  const canSeeAttendance = canAnyIn(capabilities, "attendance");
  const canSeeOrganization = canAnyIn(capabilities, "organization");

  const today = nowInOrgTimezone().format("YYYY-MM-DD");

  const usersQuery = useFetchUser({ page_size: 1 });
  const totalEmployees = usersQuery.data?.count ?? 0;

  const todaysTimeSheetQuery = useFetchTimeSheet(
    { date: today, page_size: ATTENDANCE_SNAPSHOT_SIZE },
    { enabled: attendanceEnabled && canSeeAttendance },
  );
  const checkedInToday = useMemo(
    () =>
      (todaysTimeSheetQuery.data?.results ?? []).filter(
        (ts) => ts.check_in_entry,
      ).length,
    [todaysTimeSheetQuery.data],
  );

  // Same params as PendingLeaveRequests's own query so the two share one
  // cached request instead of firing it twice.
  const pendingLeaveQuery = useFetchLeaveRequests(
    { status: "REQUESTED", page_size: 5, ordering: "-created_at" },
    true,
    { enabled: leaveEnabled && canViewLeaveRequests },
  );
  const pendingLeaveCount = pendingLeaveQuery.data?.count ?? 0;

  // Same params as TasksNeedingAttention's overdue query, for the same reason.
  const overdueTasksQuery = useFetchTasks(
    { is_overdue: true, page_size: 5, ordering: "due_date" },
    true,
    { enabled: tasksEnabled && canManageTasks },
  );
  const overdueTasksCount = overdueTasksQuery.data?.count ?? 0;

  const advancesQuery = useFetchAdvances(
    { page_size: ADVANCES_SNAPSHOT_SIZE },
    { enabled: salariesEnabled && canManageAdvances },
  );
  const outstandingAdvancesCount = useMemo(
    () =>
      (advancesQuery.data?.results ?? []).filter((a) => !a.is_settled).length,
    [advancesQuery.data],
  );

  const branchesQuery = useFetchBranches(
    { page_size: 1, is_active: true },
    { enabled: canSeeOrganization },
  );
  const departmentsQuery = useFetchDepartments(
    { page_size: 1 },
    { enabled: canSeeOrganization },
  );

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-6 p-6 pt-0">
        <PageHeader
          title="Admin Dashboard"
          description={
            (user?.first_name
              ? `Welcome back, ${user.first_name}. `
              : "Welcome back. ") +
            "Here's today's snapshot across the organization."
          }
        />

        {/* Approvers land here on sign-in, so pending leave and overtime
            decisions have to show here too, not only under /user (T-165). */}
        {receivesApprovals(capabilities) && <SupervisorAccessBanner />}

        {/* KPI row — flex, not a fixed grid: however many cards pass their
            gates, they grow to fill the row instead of leaving the empty
            column-tracks a fixed grid-cols would when fewer are shown. */}
        <div className="flex flex-wrap gap-4">
          {canSeeEmployees && (
            <Link to="/admin/users" className="block no-underline flex-1 min-w-[160px]">
              <StatCard
                label="Employees"
                value={totalEmployees}
                loading={usersQuery.isPending}
                className={cardClassName}
              />
            </Link>
          )}

          {attendanceEnabled && canSeeAttendance && (
            <Link
              to="/admin/attendance/timesheets"
              className="block no-underline flex-1 min-w-[160px]"
            >
              <StatCard
                label="Present Today"
                value={`${checkedInToday}/${totalEmployees}`}
                loading={todaysTimeSheetQuery.isPending || usersQuery.isPending}
                className={cardClassName}
              />
            </Link>
          )}

          {leaveEnabled && canViewLeaveRequests && (
            <Link
              to="/admin/leave/requests"
              className="block no-underline flex-1 min-w-[160px]"
            >
              <StatCard
                label="Pending Leave"
                value={pendingLeaveCount}
                loading={pendingLeaveQuery.isPending}
                className={cardClassName}
              />
            </Link>
          )}

          {tasksEnabled && canManageTasks && (
            <Link to="/admin/tasks" className="block no-underline flex-1 min-w-[160px]">
              <StatCard
                label="Overdue Tasks"
                value={overdueTasksCount}
                loading={overdueTasksQuery.isPending}
                className={cardClassName}
              />
            </Link>
          )}

          {salariesEnabled && canManageAdvances && (
            <Link
              to="/admin/payroll/advances"
              className="block no-underline flex-1 min-w-[160px]"
            >
              <StatCard
                label="Outstanding Advances"
                value={outstandingAdvancesCount}
                loading={advancesQuery.isPending}
                className={cardClassName}
              />
            </Link>
          )}

          {canSeeOrganization && (
            <Link
              to="/admin/organization/branches"
              className="block no-underline flex-1 min-w-[160px]"
            >
              <StatCard
                label="Branches / Departments"
                value={`${branchesQuery.data?.count ?? 0} / ${departmentsQuery.data?.count ?? 0}`}
                loading={branchesQuery.isPending || departmentsQuery.isPending}
                className={cardClassName}
              />
            </Link>
          )}
        </div>

        {/* Needs attention */}
        {((leaveEnabled && canViewLeaveRequests) ||
          (tasksEnabled && canManageTasks)) && (
          <div className="flex flex-wrap gap-4">
            {leaveEnabled && canViewLeaveRequests && (
              <div className="flex-1 min-w-[320px]">
                <PendingLeaveRequests />
              </div>
            )}
            {tasksEnabled && canManageTasks && (
              <div className="flex-1 min-w-[320px]">
                <TasksNeedingAttention />
              </div>
            )}
          </div>
        )}

        {/* Coming up / catch-up — both belong to Attendance's reach */}
        {canSeeAttendance && (
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[320px]">
              <UpcomingHolidays />
            </div>
            {attendanceEnabled && (
              <div className="flex-1 min-w-[320px]">
                <TodaysAttendance />
              </div>
            )}
          </div>
        )}
      </div>
    </SidebarInset>
  );
}
